import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Phone, MessageCircle, ArrowRight, ShieldCheck, CheckCircle2, Star, ChevronRight, FileText, Search, Scale, Gavel, Handshake, Building2, Sparkles } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { BRAND, SERVICES, INDUSTRIES, WHY_US, PROCESS, STATS, FAQS, TESTIMONIALS } from "@/lib/brand";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ICON_MAP = {
  FileText, Search, Scale, Gavel, Handshake, Building2,
  Heart: Icons.Heart, ShieldCheck: Icons.ShieldCheck, Car: Icons.Car, Plane: Icons.Plane,
  Flame: Icons.Flame, Ship: Icons.Ship, Home: Icons.Home, Briefcase: Icons.Briefcase, Activity: Icons.Activity,
  Award: Icons.Award, Eye: Icons.Eye, Lock: Icons.Lock, Users: Icons.Users, Zap: Icons.Zap, UserCheck: Icons.UserCheck, FileLock2: Icons.FileLock2,
};

const HERO_IMG = "https://images.unsplash.com/photo-1709787627975-9cb37bbeca60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxyZWxpZXZlZCUyMGZhbWlseSUyMGhvbWV8ZW58MHx8fHwxNzgyMzA0MDc2fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  useEffect(() => { document.title = `${BRAND.name} — ${BRAND.tagline}`; }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-50" />
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6 animate-float-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-brand/10 text-emerald-brand text-xs font-semibold tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> Independent · IRDAI-aware · Gujarat-headquartered
            </div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-[58px] leading-[1.05] mt-5 text-slate-900">
              Your Claim. <span className="text-brand-gradient">Our Expertise.</span><br />
              Your Peace of Mind.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
              Independent insurance claim advisory in Ahmedabad. We help policyholders build stronger claim files,
              challenge wrongful rejections, and coordinate with insurers — across health, life, motor and general insurance.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-book-consultation" className="btn-gold text-sm">
                Book a free consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://fact-based-challenge.emergent.host"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-ai-eval"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white transition-all text-sm"
                style={{ background: "linear-gradient(135deg,#0A2540 0%,#0F9D58 100%)", boxShadow: "0 8px 22px rgba(15,157,88,0.28)" }}
              >
                <Sparkles className="w-4 h-4 text-gold" /> AI Claim Evaluation
              </a>
              <Link to="/register" data-testid="hero-upload-documents" className="btn-primary text-sm">
                Upload documents <ChevronRight className="w-4 h-4" />
              </Link>
              <a href={BRAND.telUrl} data-testid="hero-talk-expert" className="btn-outline text-sm">
                <Phone className="w-4 h-4" /> Talk to an expert
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {STATS.slice(0, 3).map((s) => (
                <div key={s.label} className="">
                  <div className="font-heading font-bold text-2xl md:text-3xl text-navy">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-[28px] overflow-hidden brand-shadow-lg aspect-[5/4]">
              <img src={HERO_IMG} alt="Family at peace after claim resolution" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A2540]/40 via-transparent to-transparent" />
            </div>
            <div className="glass absolute -bottom-6 -left-4 md:-left-10 p-5 rounded-2xl w-72 hidden md:block">
              <div className="flex items-center gap-2 text-emerald-brand">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold text-sm">Recently settled</span>
              </div>
              <div className="mt-2 text-sm text-slate-700 leading-snug">
                ₹4.2L health claim, initially rejected as pre-existing — settled in 6 weeks.
              </div>
              <div className="mt-2 text-[11px] text-slate-500 uppercase tracking-wider">Case CH-2026-018</div>
            </div>
            <div className="glass absolute -top-6 -right-2 md:-right-8 p-4 rounded-2xl flex items-center gap-3 hidden md:flex">
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-heading font-bold">
                4.9
              </div>
              <div>
                <div className="flex text-gold">
                  {[0,1,2,3,4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <div className="text-[11px] text-slate-600">Trusted by 500+ families</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="border-y border-slate-200/70 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-6 flex flex-wrap items-center justify-between gap-6">
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Working with claims across</span>
          <div className="flex flex-wrap gap-x-8 gap-y-2 font-heading font-semibold text-slate-600 text-sm">
            <span>HDFC ERGO</span><span>Star Health</span><span>ICICI Lombard</span><span>LIC</span><span>Bajaj Allianz</span><span>Niva Bupa</span><span>Tata AIG</span><span>New India</span>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-[1280px] mx-auto px-5 md:px-8 py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow">What we do</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3 leading-tight">
              Built for policyholders.<br />
              <span className="text-emerald-brand">Backed by 20+ years</span> in claims.
            </h2>
          </div>
          <Link to="/services" data-testid="services-view-all" className="text-sm font-semibold text-navy hover:text-emerald-brand inline-flex items-center gap-1.5">
            View all services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => {
            const Icon = ICON_MAP[s.icon] || FileText;
            return (
              <div key={s.slug} data-testid={`service-card-${s.slug}`} className="brand-card p-7 group">
                <div className="w-12 h-12 rounded-xl bg-navy/5 text-navy flex items-center justify-center group-hover:bg-navy group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-xl mt-5">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.short}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-brand mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Why Accurate Claims</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl mt-3 leading-tight">
              Independent. Transparent.<br />Always on your side.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We do not sell insurance and we do not earn commissions from insurers. Our only client is you,
              the policyholder. That independence is what makes our advice honest and our representation effective.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="whyus-whatsapp" className="btn-emerald text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp us
              </a>
              <Link to="/about" data-testid="whyus-about-link" className="btn-outline text-sm">About the firm</Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-4">
            {WHY_US.map((w) => {
              const Icon = ICON_MAP[w.icon] || ShieldCheck;
              return (
                <div key={w.title} className="brand-card p-5 flex flex-col items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gold/15 text-gold flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-heading font-semibold text-[15px] leading-tight">{w.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow justify-center inline-flex">Our process</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3 leading-tight">
            Seven steps. <span className="text-emerald-brand">Total clarity.</span>
          </h2>
          <p className="mt-3 text-slate-600">Every step is time-stamped on your client portal so you always know where your case stands.</p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-5">
            {PROCESS.map((p, i) => (
              <div key={p.step} className="brand-card p-5 relative">
                <div className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center font-heading font-bold relative z-10">
                  {p.step}
                </div>
                <div className="font-heading font-semibold text-[15px] mt-4 leading-tight">{p.title}</div>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-10" />
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 relative">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
            <div className="md:col-span-7">
              <div className="eyebrow !text-gold">Industries we serve</div>
              <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3 leading-tight">
                Every line of insurance.<br />
                <span className="text-gold">Every kind of claim.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-slate-300 leading-relaxed">
              From a delayed motor settlement to a complex corporate fire claim, our team has handled it.
              Independent guidance across the full spectrum of policies.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map((ind) => {
              const Icon = ICON_MAP[ind.icon] || ShieldCheck;
              return (
                <div key={ind.name} data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g,'-')}`} className="glass-dark p-5 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gold/20 text-gold flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-heading font-semibold text-lg">{ind.name}</div>
                  </div>
                  <p className="text-sm text-slate-300 mt-3 leading-relaxed">{ind.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="brand-card p-7 text-center">
              <div className="font-heading font-bold text-4xl md:text-5xl text-navy">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow justify-center inline-flex">Voices of our clients</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3">Real stories. <span className="text-emerald-brand">Real settlements.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="brand-card p-7">
                <div className="flex text-gold mb-3">
                  {[0,1,2,3,4].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-slate-700 leading-relaxed text-[15px]">"{t.body}"</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <div className="font-heading font-semibold">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="eyebrow">Frequently asked</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3 leading-tight">
            Questions, answered honestly.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Don't see your question? Send us a WhatsApp message and our team will reply within working hours.
          </p>
          <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="faq-whatsapp" className="btn-emerald text-sm mt-6 inline-flex">
            <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
          </a>
        </div>
        <div className="md:col-span-7">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border border-slate-200 rounded-2xl mb-3 px-5 bg-white"
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-[15px] hover:no-underline py-4">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-[14.5px] pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pb-20">
        <div className="rounded-[28px] bg-gradient-to-br from-[#0A2540] via-[#0F2D4F] to-[#11365E] p-10 md:p-14 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-dot-grid opacity-10" />
          <div className="grid md:grid-cols-12 gap-8 items-center relative">
            <div className="md:col-span-7">
              <div className="eyebrow !text-gold">Speak to an expert</div>
              <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3 leading-tight">
                Have a claim that needs a second opinion?
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                Share your case details — a senior advisor will review and respond within 24 working hours.
                No charges for the first consultation.
              </p>
            </div>
            <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
              <Link to="/contact" data-testid="cta-contact-btn" className="btn-gold text-sm">
                Book consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-btn" className="btn-emerald text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <WhatsAppFAB />
    </div>
  );
}
